import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import type { CreateAPIKeyResponse } from '@/types';

interface ApiKeyDisplayModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKeyData: CreateAPIKeyResponse | null;
  userEmail: string;
  userName?: string;
}

export const ApiKeyDisplayModal: React.FC<ApiKeyDisplayModalProps> = ({
  open,
  onOpenChange,
  apiKeyData,
  userEmail,
  userName,
}) => {
  const [hasSaved, setHasSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    if (!apiKeyData?.api_key) return;

    try {
      await navigator.clipboard.writeText(apiKeyData.api_key);
      setCopied(true);
      toast({
        title: 'Copied to clipboard',
        description: 'API key has been copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    if (!hasSaved) {
      if (
        !confirm(
          'Are you sure you want to close this window? Your API key will be lost forever if you haven\'t saved it. This cannot be undone.'
        )
      ) {
        return;
      }
    }
    setHasSaved(false);
    setCopied(false);
    onOpenChange(false);
  };

  if (!apiKeyData) return null;

  const { api_key: secretKey, api_key_prefix, api_key_expires_at } = apiKeyData;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Save Your API Key Now
          </DialogTitle>
          <DialogDescription>
            This is the only time you will see this API key. Store it securely.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning Alert */}
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>IMPORTANT:</strong> This API key will only be shown ONCE. Make sure to copy
              it now and store it securely. You won't be able to see it again!
            </AlertDescription>
          </Alert>

          {/* Service Account Info */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Service Account Created</h4>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-success" />
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{userEmail}</span>
              </div>
              {userName && (
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-success" />
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{userName}</span>
                </div>
              )}
            </div>
          </div>

          {/* API Key Display */}
          <div className="space-y-2">
            <Label htmlFor="api-key">Your API Key</Label>
            <div className="flex gap-2">
              <Input
                id="api-key"
                readOnly
                value={secretKey}
                className="font-mono text-sm bg-muted"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Key Details */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Key Details</h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div>
                <span className="font-medium">Key Prefix:</span> {api_key_prefix}
              </div>
              {api_key_expires_at && (
                <div>
                  <span className="font-medium">Expires:</span> {formatDate(api_key_expires_at)}
                </div>
              )}
            </div>
          </div>

          {/* Usage Example */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Usage Example</h4>
            <div className="bg-muted p-3 rounded-md">
              <code className="text-xs font-mono">
                curl -H "X-API-Key: {secretKey.substring(0, 20)}..." \<br />
                &nbsp;&nbsp;{window.location.origin}/api/datasets
              </code>
            </div>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="saved-confirmation"
              checked={hasSaved}
              onCheckedChange={(checked) => setHasSaved(checked as boolean)}
            />
            <label
              htmlFor="saved-confirmation"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have saved this API key securely
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={handleClose} disabled={!hasSaved}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
